import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import Hero from '../../../../components/Hero';
import Ticket from '../../../../components/Ticket';
import { String } from '../../../../components/string';
import NavButton from '../../../../components/_Atoms/NavButton';
import PageTransition from '../../../../components/_Theme/PageTransition';
import TextBlockWithBorders from '../../../../components/textBlockWithBorders';
import cookies from '../../../../lib/cookies';
import { withVenueAndStrings } from '../../../../lib/wrappers';
import useBookingParams from '../../../../hooks/useBookingParams';
import { useGetReservationsQuery } from '../../../../services/bookingApi';
import {
  useGetVenueTitle,
  useGetVenueName,
  s3Api,
  useGetCountryCode,
} from '../../../../services/s3Api';
import Layout from '../../../../components/layout';
import useSearchParams from '../../../../hooks/useSearchParams';
import { searchWithoutPersonalParams } from '../../../../lib/utils/personalParams';
import Hotjar from '@hotjar/browser';
import { hotjarSiteId, hotjarVersion } from '../../../../lib/utils/hotjar';
import { GetServerSidePropsContext } from 'next';
import { useDispatch } from 'react-redux';
import { resetParams, setLang } from '../../../../services/paramsSlice';
import { wrapper } from '../../../../lib/store';
import useRehydrationComplete from '../../../../lib/utils/rehydrate';
import { setupGTM } from '../../../../lib/utils/gtmTag';
import { states } from '../../../../lib/booking/reservation-states';
import { usePageTitle } from '../../../../hooks/usePageTitle';
import { useGetString } from '../../../../hooks/useGetString';

// if (hotjarSiteId) {
//   Hotjar.init(hotjarSiteId, hotjarVersion);
// }

type Props = PropsWithStrings<{ preview: boolean; locale?: string }>;

export default function Index({ strings, preview, locale = '', ...rest }: Props) {
  const router = useRouter();
  const dispatch = useDispatch();
  const { setZone } = useBookingParams();
  const { store, props } = wrapper.useWrappedStore(rest);
  const countryCode = useGetCountryCode();
  const [gtmInitialized, setGtmInitialized] = useState(false);
  const getString = useGetString();
  const { PageTitle } = usePageTitle(getString('make-reservation'));

  function hasOnlyVenueId(obj) {
    const keys = Object.keys(obj);
    return keys.length === 1 && keys[0] === 'venueId';
  }

  const isRehydrated = useRehydrationComplete();

  sessionStorage.setItem('hasVisitedSuggestions', 'false');

  useEffect(() => {
    if (isRehydrated) {
      // For example, invalidate the RTK Query cache for certain tags if needed
      dispatch(s3Api.util.invalidateTags(['Venue']));
    }
  }, [isRehydrated, dispatch]);

  // Reset the state to initial and the set the language in Redux state based on the locale
  useEffect(() => {
    if (hasOnlyVenueId(router.query)) {
      dispatch(resetParams());
    }
    // dispatch(setLang(locale));
  }, [locale, dispatch]);

  // Resetting zone to indoors when landing on index page
  useEffect(() => {
    setZone('indoors');
  }, []);

  const {
    replace,
    query: { venueId },
  } = router;

  const searchParams = useSearchParams({ asString: true });
  const venueTitle = useGetVenueTitle(locale);
  const venueName = useGetVenueName(locale);

  const {
    data: reservations,
    isLoading: isLoadingReservations,
    isFetching: isFetchingReservations,
    isSuccess,
  } = useGetReservationsQuery(cookies.getReservationIds(), {
    refetchOnMountOrArgChange: true,
  });

  const isLoading = isLoadingReservations;

  const sortedReservations =
    isSuccess && reservations?.results
      ? reservations.results
          .filter(
            (reservation) =>
              !(
                reservation?.waitlistQuartiles?.length > 0 && reservation.state === states.CANCELLED
              ),
          )
          // In the order the tickets read: business day, then booked time.
          .sort(
            (a, b) =>
              (a.dateString ?? '').localeCompare(b.dateString ?? '') ||
              (a.startQuartile ?? 0) - (b.startQuartile ?? 0),
          )
      : [];

  useEffect(() => {
    if (!isLoading && isSuccess && !reservations?.results?.length) {
      console.log('No reservations found, redirecting to new reservation page', searchParams);
      replace(`/venue/${venueId}/new-reservation/count${searchWithoutPersonalParams(searchParams)}`);
    }
  }, [reservations?.results?.length, replace, venueId, isLoading, isSuccess, searchParams]);

  return (
    <Layout>
      <PageTitle />
      <>
        <Hero title={<String id="make-reservation" />} subtitle={venueTitle} />
        {/* Using <String> component in TextBlockWithBorders causes hydration error for some reason. */}
        {venueName && <TextBlockWithBorders text={'venue.welcome-text'} venueTitle={venueName} />}
        <div className=" grow flex flex-col rounded-lg px-5 pb-24">
          <PageTransition
            animationInitial={{ y: '100vh' }}
            animate={{ y: 0 }}
            className="gap-12 mt-5"
          >
            <>
              {!isLoading
                ? sortedReservations.map((reservation, key) => {
                    // const reservationVenue = venues.find((v) => v.id === reservation.venueId);
                    return (
                      <React.Fragment
                        key={key}
                      >
                        <Ticket
                          venueId={reservation.venueId}
                          link={true}
                          locale={locale}
                          bookingId={reservation.objectId}
                          peopleHeader={getString('labels.count')}
                          people={getString('labels.persons')}
                          reservation={reservation}
                          editable={true}
                          className={`${reservation?.state === states.CANCELLED ? 'pointer-events-none' : ''}`}
                        />
                      </React.Fragment>
                    );
                  })
                : null}
            </>
          </PageTransition>
        </div>
        <div className="px-4 py-5 pb-8 fixed rounded-b-lg bottom-0 z-50 flex flex-col items-center justify-center text-center w-full inheritWidth">
          <NavButton
            dataTestId="new-reservation"
            color="gold"
            href={{
              pathname: `/${locale}/venue/${venueId}/new-reservation/count`,
              // query: { ...params, seatings: 2, bookingId: null },
            }}
          >
            <String id="button.new-reservation" />
          </NavButton>
        </div>
      </>
    </Layout>
  );
}

// export const getStaticPaths = getStaticVenuePaths();

// export const getStaticProps = withVenueAndStrings(
//   ({ venue, strings }) =>
//     async ({ preview = false, locale }) => {
//       return {
//         props: { strings, preview, locale },
//         // If webhooks isn't setup then attempt to re-generate in 1 minute intervals
//         revalidate: DEFAULT_REVALIDATION,
//       };
//     },
// );

export const getServerSideProps = withVenueAndStrings(
  ({ strings }) =>
    async (context: GetServerSidePropsContext) => {
      const { preview = false, locale } = context;
      return {
        props: { strings, preview, locale },
      };
    },
);
